import React, {useContext, useEffect, useState} from 'react'
import {DropdownInput, DropdownOption, IconButton, Modal} from "atmosfair-ui";
import {VALIDATE_FORM_STARTING_NUMBER} from "../../../constants/default-values";
import SaveBundleCreateJourneyDialogue
    from "../../../components/save-bundle-create-journey-dialogue/SaveBundleCreateJourneyDialogue";
import {CreateBundleProps} from "../../../api/model/bundle";
import {useCreateBundleMutation, useUpdateBundleMutation} from "../../../redux/reducer/api";
import {AllActivityTypesArray} from "../../../types-enums/activity";
import {useAppSelector} from "../../../redux/reducer/store";
import {GENERAL_BACKEND_RESPONSE, GeneralBackendResponse} from "../../../api/model/common";
import {Customer} from "../../../api/model/customer";
import {DurationProps} from "../../../types-enums/activity-common-properties";
import {convertPersistBundlesButtonOptionsToDropdownFormat} from "../../../utils/converter/dropdown";
import {useTranslation} from "react-i18next";
import {DEFAULT_TRAVEL_COMPONENTS, updateTravelComponentsProps} from "../../../redux/reducer/travel-components";
import {useDispatch} from "react-redux";
import {DEFAULT_JOURNEY, updateJourneyProps} from "../../../redux/reducer/journey";
import {CommonBundleProps} from "../../../redux/reducer/common";
import {isCreateBundleSuccessResponse} from "../../../utils/check/type-guards";
import {convertBackendStringDateToDateTime} from "../../../utils/converter/dates";
import {BundleType} from "../common";
import {CustomerFormProps, initialCustomer} from "../../../components/forms/customer-form/CustomerForm";
import {AgencyProps} from "../../../api/model/agency";
import {isEqual} from "lodash";
import {InstanceContext} from "../../../components/protected-layout/ProtectedLayout";

interface SaveBundlesButtonProps {
    whatToSave: BundleType,
    activities: AllActivityTypesArray,
    bundleHasBeenLoaded: boolean,
    disabled?: boolean,
}

export enum BundlesPersistingOptions {
    save = 'save', update = "update"
}

const setTooltip = (whatToSave: SaveBundlesButtonProps["whatToSave"], noAgencyCreated: boolean, disabledNotOnProduction: boolean) => {
    if (disabledNotOnProduction) return "TOOLTIP.COMMON.DISABLED_FUNCTIONALITY_DEV_INSTANCE"
    if (noAgencyCreated) return "TOOLTIP.COMMON.CRETE_AGENCY_SAVE_BUNDLE"
    switch (whatToSave) {
        case BundleType.journey:
            return "TOOLTIP.JOURNEY.SAVE_JOURNEY"
        case BundleType.travelComponents:
            return "TOOLTIP.TRAVEL_COMPONENTS.SAVE_TRAVEL_COMPONENTS"
        default:
            whatToSave satisfies never
    }
}

export enum WHICH_ACTION_BUNDLE {
    create, update
}

const SaveBundlesButton = ({
                               whatToSave,
                               activities,
                               bundleHasBeenLoaded,
                               disabled,
                           }: SaveBundlesButtonProps) => {
    const [bundleData, setBundleData] = useState<CreateBundleProps | null>(null)
    const [bundleAction, setBundleAction] = useState<BundlesPersistingOptions | null>(null)
    const [validateForm, setValidateForm] = useState(VALIDATE_FORM_STARTING_NUMBER)
    const [saveBundlesModalVisible, setSaveBundlesModalVisible] = useState(false)
    const {disableButtonOnNotProduction} = useContext(InstanceContext)
    const {
        bundleId: travelComponentsBundleId,
        activities: travelComponentsActivities,
        name: travelComponentsName,
        description: travelComponentsDescription,
        customer: travelComponentsCustomer,
        agency: travelComponentsAgency,
    } = useAppSelector(state => state.travelComponents)
    const {
        bundleId: journeyBundleId,
        activities: journeyActivities,
        name: journeyName,
        description: journeyDescription,
        customer: journeyCustomer,
        journeyDuration: journeyDurationFromStore,
        personCount: journeyPersonCount,
        agency: journeyAgency,
    } = useAppSelector(state => state.journey)
    const {agencies, userData} = useAppSelector(state => state.user)
    const {i18n} = useTranslation()
    const [createBundle, {
        data: createBundleResponse,
        isLoading: isLoadingCreateBundle,
    }] = useCreateBundleMutation()
    const [updateBundle, {
        data: updateBundleResponse,
        isLoading: isLoadingUpdateBundle,
    }] = useUpdateBundleMutation()
    const noAgencyCreated = agencies?.length === 0
    const tooltip = setTooltip(whatToSave, noAgencyCreated, disableButtonOnNotProduction)
    const emptyTravelComponents = isEqual({
        activities: travelComponentsActivities,
        name: travelComponentsName,
        description: travelComponentsDescription,
        customer: travelComponentsCustomer,
        bundleId: travelComponentsBundleId,
    }, DEFAULT_TRAVEL_COMPONENTS)
    const emptyJourney = isEqual({
        bundleId: journeyBundleId,
        name: journeyName,
        description: journeyDescription,
        activities: journeyActivities,
        customer: journeyCustomer,
        journeyDuration: journeyDurationFromStore,
        personCount: journeyPersonCount,
    }, DEFAULT_JOURNEY)
    const dispatch = useDispatch()

    useEffect(() => {
        if (createBundleResponse?.status === GENERAL_BACKEND_RESPONSE.success) {
            const {response} = createBundleResponse
            _updateBundleProps(response, WHICH_ACTION_BUNDLE.create)
        }
    }, [createBundleResponse]);

    useEffect(() => {
        if (updateBundleResponse?.status === GENERAL_BACKEND_RESPONSE.success) {
            const {response} = updateBundleResponse
            _updateBundleProps(response, WHICH_ACTION_BUNDLE.update)
        }
    }, [updateBundleResponse])

    const _updateBundleProps = (response: GeneralBackendResponse | undefined, whichAction: WHICH_ACTION_BUNDLE) => {
        if (isCreateBundleSuccessResponse(response) && bundleData !== null) {
            const {bundleId, created, mapOfActivityIds} = response
            const {name, description, customer, agency} = bundleData
            const bundleProps: CommonBundleProps = {
                name,
                userId: userData?.id ?? null,
                description,
                customer,
                agency,
                bundleId,
                created: convertBackendStringDateToDateTime(created),
                updated: null,
            }
            switch (whatToSave) {
                case BundleType.journey:
                    dispatch(updateJourneyProps({bundleProps, mapOfActivityIds, whichAction}))
                    break;
                case BundleType.travelComponents:
                    dispatch(updateTravelComponentsProps({bundleProps, mapOfActivityIds, whichAction}))
                    break;
                default:
                    whatToSave satisfies never
            }
        }
        _resetForm()
    }

    const _resetForm = () => {
        setBundleAction(null)
        setSaveBundlesModalVisible(false)
        setValidateForm(VALIDATE_FORM_STARTING_NUMBER)
    }

    const _onSaveBundle = (selection: DropdownOption[] | null) => {
        setSaveBundlesModalVisible(true)
        if (selection === null) {
            setBundleAction(null)
        } else {
            const {raw} = selection[0]
            const key = raw.key as BundlesPersistingOptions
            setBundleAction(key)
        }
    }

    const _setConfirmButton = () => {
        if (bundleAction === null) return "GENERAL.SAVE"
        switch (bundleAction) {
            case BundlesPersistingOptions.save:
                return "GENERAL.SAVE"
            case BundlesPersistingOptions.update:
                return "GENERAL.UPDATE_DATA"
            default:
                bundleAction satisfies never;
        }
    }

    const _onFormIsValid = (data: CreateBundleProps) => {
        const createBundleData = {...data}
        switch (whatToSave) {
            case BundleType.journey:
                if (journeyBundleId !== null && bundleAction === BundlesPersistingOptions.update) {
                    updateBundle({
                        id: journeyBundleId,
                        ...data
                    })
                } else {
                    createBundle(createBundleData)
                }
                break
            case BundleType.travelComponents:
                if (travelComponentsBundleId !== null && bundleAction === BundlesPersistingOptions.update) {
                    updateBundle({
                        id: travelComponentsBundleId,
                        ...data
                    })
                } else {
                    createBundle(createBundleData)
                }
                break;
            default:
                whatToSave satisfies never
        }
        setBundleData(data)
    }

    const _setModalHeadline = () => {
        const pathToTitle = "MODALS.SAVE_BUNDLES"
        if (bundleAction !== null) {
            switch (bundleAction) {
                case BundlesPersistingOptions.save:
                    return whatToSave === BundleType.journey
                        ? `${pathToTitle}.SAVE_JOURNEY_TITLE`
                        : `${pathToTitle}.SAVE_TRAVEL_COMPONENTS_TITLE`;

                case BundlesPersistingOptions.update:
                    return whatToSave === BundleType.journey
                        ? `${pathToTitle}.UPDATE_JOURNEY_TITLE`
                        : `${pathToTitle}.UPDATE_TRAVEL_COMPONENTS_TITLE`
                default:
                    bundleAction satisfies never;
            }
        }
        switch (whatToSave) {
            case BundleType.journey:
                return `${pathToTitle}.SAVE_JOURNEY_TITLE`
            case BundleType.travelComponents:
                return `${pathToTitle}.SAVE_TRAVEL_COMPONENTS_TITLE`
        }
    }

    const _setDefaultValues = () => {
        let description: string | null = null
        let name: string | number | null = null
        let customer: Customer | null = null
        let agency: AgencyProps | null = null
        let journeyDuration: DurationProps | null = null
        let personCount: number | null = null

        switch (whatToSave) {
            case BundleType.travelComponents:
                description = travelComponentsDescription
                name = travelComponentsName
                customer = travelComponentsCustomer
                agency = travelComponentsAgency
                journeyDuration = null
                personCount = null
                break
            case BundleType.journey:
                description = journeyDescription
                name = journeyName
                customer = journeyCustomer
                agency = journeyAgency
                journeyDuration = journeyDurationFromStore
                personCount = journeyPersonCount
                break
            default:
                whatToSave satisfies never;
        }

        let defaultCustomer: CustomerFormProps["defaultCustomer"]
        const composedInitialCustomer: Customer = {
            ...initialCustomer,
            houseNumber: "",
            street: "",
            company: null,
            phoneNumber: "",
            city: "",
            zipCode: "",
            additionalAddressInformation: "",
        }
        if (bundleAction === BundlesPersistingOptions.save) return {
            description: "",
            name: "",
            customer: composedInitialCustomer,
            agency,
            journeyDuration,
            personCount,
        }
        if (customer !== null) {
            defaultCustomer = {
                id: customer.id,
                firstName: customer.firstName,
                lastName: customer.lastName,
                company: customer.company,
                email: customer.email,
                street: customer.street,
                houseNumber: customer.houseNumber,
                zipCode: customer.zipCode,
                city: customer.city,
                additionalAddressInformation: customer.additionalAddressInformation,
                phoneNumber: "",
            }
        } else {
            defaultCustomer = composedInitialCustomer
        }

        return {
            description: description ?? "",
            name: name !== null ? name.toString() : "",
            customer: defaultCustomer,
            agency,
            journeyDuration,
            personCount,
        }
    }

    const _setDisableButton = () => {
        if (disabled || noAgencyCreated || disableButtonOnNotProduction) return true
        switch (whatToSave) {
            case BundleType.journey:
                return emptyJourney
            case BundleType.travelComponents:
                return emptyTravelComponents
            default:
                whatToSave satisfies never
        }
    }

    return <>
        {bundleHasBeenLoaded ? <DropdownInput
                useSelection={false}
                readOnly={_setDisableButton()}
                label={whatToSave === BundleType.journey ? "LABELS.JOURNEY.UPDATE_OR_SAVE_JOURNEY" : "LABELS.TRAVEL_COMPONENTS.UPDATE_OR_SAVE_TRAVEL_COMPONENTS"}
                options={convertPersistBundlesButtonOptionsToDropdownFormat(BundlesPersistingOptions, i18n)}
                iconOnlyColor={"primary"}
                iconOnlyIcon={"save"}
                iconOnlySize={"large"}
                setSelection={selection => _onSaveBundle(selection)}
                iconOnly={true}/> :
            <IconButton
                disabled={_setDisableButton()}
                tooltip={tooltip}
                color={"primary"}
                size={"large"}
                onlyIcon={true}
                icon={"save"}
                onClick={() => _onSaveBundle(null)}/>}
        {agencies && <Modal
            closeButtonText={"GENERAL.ABORT"}
            onClose={_resetForm}
            onEnterKeyPressed={() => setValidateForm(validateForm + 1)}
            onBackdropClick={_resetForm}
            headline={_setModalHeadline()}
            showPaper={true}
            onConfirm={() => setValidateForm(validateForm + 1)}
            confirmButtonText={_setConfirmButton()}
            showSpinner={isLoadingCreateBundle || isLoadingUpdateBundle}
            show={saveBundlesModalVisible}>
            <SaveBundleCreateJourneyDialogue
                bundleHasBeenLoaded={bundleHasBeenLoaded}
                personCountFromSiteTitle={journeyPersonCount}
                journeyDurationFromSiteTitle={journeyDurationFromStore}
                persistJourneyInBackend={whatToSave === BundleType.journey}
                persistTravelComponentsInBackend={whatToSave === BundleType.travelComponents}
                defaultValues={_setDefaultValues()}
                showCompany={false}
                hideTravelComponents={whatToSave === BundleType.journey}
                createJourneyFromTravelComponents={false}
                onFormIsValid={_onFormIsValid}
                validateForm={validateForm}
                agencies={agencies}
                activities={activities}/>
        </Modal>}
    </>
}

export default SaveBundlesButton